import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, type ArticleData } from "@/mocks/articles";

export default function ArticleLayout() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article: ArticleData | undefined = slug ? articles[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">Article Not Found</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">Return to Homepage</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-foreground-700">{article.title.length > 60 ? article.title.substring(0, 60) + "..." : article.title}</span>
          </nav>

          {/* Article Header */}
          <header>
            <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4">
              {article.tag}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-foreground-500 mb-8">
              <span>Updated: August 2026</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>By BaseTaoSheet Editorial Team</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-10">
            <img
              src={article.featuredImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Table of Contents */}
          <nav className="bg-background-100 rounded-xl p-5 mb-10 border border-background-200/60">
            <h2 className="font-heading text-lg text-foreground-900 mb-3">Table of Contents</h2>
            <ol className="space-y-1.5 text-sm text-foreground-600">
              {article.sections.map((section, idx) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="hover:text-primary-600 transition-colors">
                    {idx + 1}. {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Article Content */}
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">
            {article.sections.map((section, sIdx) => (
              <section key={section.id} id={section.id}>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">
                  {sIdx + 1}. {section.heading}
                </h2>
                {section.content.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-sm md:text-base leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
                {section.table && (
                  <div className="overflow-x-auto rounded-xl border border-background-200/60 my-5">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100">
                          {section.table.headers.map((header) => (
                            <th key={header} className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, rIdx) => (
                          <tr key={rIdx} className={`border-t border-background-200/40 ${rIdx % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}`}>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className={`px-4 py-2.5 ${cIdx === 0 ? "font-medium text-foreground-800 whitespace-nowrap" : "text-foreground-600"} ${cIdx >= 2 ? "text-xs" : ""}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Internal Links */}
          <div className="mt-12 p-6 rounded-xl bg-background-100 border border-background-200/60">
            <h3 className="font-heading text-lg text-foreground-900 mb-4">Continue Reading on BaseTaoSheet</h3>
            <ul className="space-y-2">
              {article.internalLinks.map((link) => (
                <li key={link.label}>
                  {link.isExternal ? (
                    <a href={link.href} target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                      <i className="ri-external-link-line"></i> {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors">
                      <i className={link.href === "/" ? "ri-home-line" : "ri-article-line"}></i> {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center p-8 rounded-2xl bg-primary-50 border border-primary-200/60">
            <h3 className="font-heading text-xl text-foreground-900 mb-3">Ready to Start Your First Haul?</h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Apply everything you have learned and start organizing your BaseTao shopping experience with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Visit Main Website
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-xl bg-secondary-100 text-secondary-800 hover:bg-secondary-200 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>
        </article>

        {/* Article Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": article.title,
              "image": article.featuredImage,
              "author": { "@type": "Organization", "name": "BaseTaoSheet Editorial Team" },
              "datePublished": "2026-08-10",
              "dateModified": "2026-08-10",
              "publisher": { "@type": "Organization", "name": "BaseTaoSheet", "url": "https://basetaosheet.site" },
              "description": article.metaDescription,
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `https://basetaosheet.site/article/${article.slug}`
              }
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}